/**
 * Collapsible "Thinking" block rendered above an assistant bubble.
 *
 * Each entry in `entries` is one of the event shapes pushed by useChat:
 *   { kind: 'route',        flow, reasoning }
 *   { kind: 'memory_load',  client[], skills[] }
 *   { kind: 'memory_write', slug, type }
 *   { kind: 'skill_save',   slug }
 *   { kind: 'tool',         name, args }
 *   { kind: 'tool_result',  name, summary?, error? }
 *   { kind: 'node',         name }
 *
 * Native <details>/<summary> for accessibility — collapsed by default while
 * the agent is still working, expanded once the user clicks. Auto-collapses
 * when the turn finishes so the final answer takes focus.
 */

import { useEffect, useRef, useState } from 'react'
import type { ThinkingEntry } from './types'

const ICON: Record<string, string> = {
  route: '🧭',
  memory_load: '📚',
  memory_write: '💾',
  skill_save: '🎓',
  tool: '🔧',
  tool_result: '✓',
  node: '→',
  reasoning_decision: '🧠',
}

// Friendly display labels for internal tool names — keep the trace
// on-brand (no upstream platform names in the UI).
const TOOL_LABELS: Record<string, string> = {
  netwatch_host_get: 'List hosts',
  netwatch_hostgroup_get: 'List host groups',
  netwatch_item_get: 'Look up items',
  netwatch_problem_get: 'Search problems',
  netwatch_trigger_get: 'Check triggers',
  netwatch_event_get: 'Check events',
  netwatch_history_get: 'Fetch metric history',
  netwatch_trend_get: 'Fetch metric trend',
  netwatch_template_get: 'Look up templates',
  netwatch_service_get: 'Check services',
  netwatch_maintenance_get: 'Check maintenance windows',
  netwatch_usermacro_get: 'Read macros',
  netwatch_at_risk_hosts: 'Scan at-risk hosts',
  netwatch_disk_forecast: 'Forecast disk space',
  netwatch_disk_io: 'Check disk I/O',
  current_user: 'Checking your account',
  mass_tag: 'Changing tags',
}

// Brand scrub for any trace text that could carry an upstream platform name
// (old persisted traces, tool args like web-search queries, result summaries).
export const debrand = (s: string): string => s.replace(/zabbix/gi, 'Netwatch')

export const toolLabel = (name: string | undefined): string =>
  TOOL_LABELS[name ?? ''] || debrand((name || '').replace(/^(netwatch|zabbix)_/, ''))

// Friendly labels for graph-node names (flow / supervisor steps).
const NODE_LABELS: Record<string, string> = {
  load_context: 'Loading context',
  classify_intent: 'Choosing flow',
  refine_goal: 'Understanding your request',
  clarify_goal: 'Clarifying details',
  load_host_context: 'Looking up the host',
  investigate: 'Investigating',
  draft_case: 'Drafting the case report',
  respond: 'Writing the answer',
  save_artifacts: 'Saving to memory',
  chat: 'Answering',
  clarify_demo: 'Demo step',
  investigate_spike: 'Spike investigation',
  mass_tag: 'Tag change',
}
export const nodeLabel = (name: string | undefined): string | undefined =>
  NODE_LABELS[name ?? ''] || name

function formatArgs(args: unknown): string {
  if (!args || typeof args !== 'object') return ''
  const entries = Object.entries(args).filter(([, v]) => v !== null && v !== undefined && v !== '')
  if (entries.length === 0) return ''
  const short = entries
    .map(([k, v]) => {
      const s = typeof v === 'string' ? v : JSON.stringify(v)
      const trimmed = s.length > 40 ? s.slice(0, 40) + '…' : s
      return `${k}=${trimmed}`
    })
    .join(', ')
  return debrand(short.length > 160 ? short.slice(0, 160) + '…' : short)
}

interface EntryProps {
  entry: ThinkingEntry
}

function Entry({ entry }: EntryProps) {
  const icon = ICON[entry.kind] || '•'

  if (entry.kind === 'reasoning_decision') {
    return (
      <div className="nw-think-row">
        <span className="nw-think-icon">{icon}</span>
        <span>
          <span className="nw-think-label">
            Reasoning {entry.needs_reasoning ? 'on' : 'off'}
          </span>{' '}
          <span className="nw-think-aside">
            — {entry.complexity}: {entry.why}
          </span>
        </span>
      </div>
    )
  }
  if (entry.kind === 'route') {
    return (
      <div className="nw-think-row">
        <span className="nw-think-icon">{icon}</span>
        <span>
          <span className="nw-think-label">Routing to</span>{' '}
          <code className="nw-think-code">{entry.flow}</code>
          {entry.reasoning && <span className="nw-think-aside"> — {entry.reasoning}</span>}
        </span>
      </div>
    )
  }
  if (entry.kind === 'memory_load') {
    const n = (entry.client?.length || 0) + (entry.skills?.length || 0)
    return (
      <div className="nw-think-row">
        <span className="nw-think-icon">{icon}</span>
        <span>
          <span className="nw-think-label">Loaded context</span>
          {n > 0 ? (
            <span className="nw-think-aside">
              {' '}— {entry.client?.length || 0} client memories, {entry.skills?.length || 0} skills
            </span>
          ) : (
            <span className="nw-think-aside"> — system prompt only</span>
          )}
        </span>
      </div>
    )
  }
  if (entry.kind === 'memory_write') {
    return (
      <div className="nw-think-row">
        <span className="nw-think-icon">{icon}</span>
        <span>
          <span className="nw-think-label">Saved {entry.type || 'memory'}:</span>{' '}
          <code className="nw-think-code">{entry.slug}</code>
        </span>
      </div>
    )
  }
  if (entry.kind === 'skill_save') {
    return (
      <div className="nw-think-row">
        <span className="nw-think-icon">{icon}</span>
        <span>
          <span className="nw-think-label">Saved skill:</span>{' '}
          <code className="nw-think-code">{entry.slug}</code>
        </span>
      </div>
    )
  }
  if (entry.kind === 'tool') {
    return (
      <div className="nw-think-row">
        <span className="nw-think-icon">{icon}</span>
        <span>
          <span className="nw-think-label">{toolLabel(entry.name)}</span>
          {formatArgs(entry.args) && (
            <span className="nw-think-aside"> · {formatArgs(entry.args)}</span>
          )}
        </span>
      </div>
    )
  }
  if (entry.kind === 'tool_result') {
    const ok = !entry.error
    return (
      <div className={`nw-think-row ${ok ? 'nw-think-ok' : 'nw-think-err'}`}>
        <span className="nw-think-icon">{ok ? '✓' : '✗'}</span>
        <span>
          <span className="nw-think-label">{toolLabel(entry.name)}</span>
          <span className="nw-think-aside">
            {' '}— {debrand(entry.error || entry.summary || 'ok')}
          </span>
        </span>
      </div>
    )
  }
  if (entry.kind === 'node') {
    return (
      <div className="nw-think-row nw-think-node">
        <span className="nw-think-icon">{icon}</span>
        <span className="nw-think-label">{nodeLabel(entry.name)}</span>
      </div>
    )
  }
  return null
}

interface Props {
  entries?: ThinkingEntry[]
  reasoning?: string
  busy?: boolean
}

export function ThinkingBlock({ entries = [], reasoning = '', busy = false }: Props) {
  const hasEntries = entries && entries.length > 0
  const hasReasoning = !!(reasoning && reasoning.trim())

  // Collapsed by default while the AI streams its thinking — the user
  // shouldn't start reading a trace that's still moving. They can expand
  // it to peek; we still stream into it either way. When the turn finishes
  // (busy: true -> false) we auto-collapse so the final answer takes focus.
  const [open, setOpen] = useState(false)
  const wasBusy = useRef(busy)
  useEffect(() => {
    if (wasBusy.current && !busy) setOpen(false)
    wasBusy.current = busy
  }, [busy])

  if (!hasEntries && !hasReasoning) return null
  const count = entries.length
  return (
    <details
      className="nw-think"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary className="nw-think-summary">
        <span className={`nw-think-dot ${busy ? 'busy' : ''}`} />
        <span className="nw-think-title">{busy ? 'Thinking' : 'Thought'}</span>
        {hasEntries && (
          <span className="nw-think-count">
            ({count} {count === 1 ? 'step' : 'steps'})
          </span>
        )}
      </summary>
      <div className="nw-think-body">
        {hasReasoning && (
          <div className="nw-reasoning">
            {reasoning}
            {busy && <span className="nw-reasoning-cursor">▍</span>}
          </div>
        )}
        {entries.map((entry, i) => (
          <Entry key={i} entry={entry} />
        ))}
      </div>
    </details>
  )
}
